import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Banner, Button, FieldSelect, FieldText, FieldTextarea, Heading, LoadingAnimation, PageHeader } from "@knowtex-ai/components";
import { ArrowLeft, Check, Mail, Search } from "iconoir-react";
import FoiaPageShell from "./FoiaPageShell";
import FoiaPatientCandidateCard from "./FoiaPatientCandidateCard";
import { parseFoiaEmail, searchFoiaPatients, fetchFoiaPatientAppointments, createFoiaRequest } from "./api";
import { formatDate } from "./foiaUtils";
import { useToast } from "../../hooks/use-toast";

const EMPTY_FORM = { trackingNumber: "", patientName: "", vamc: "", stationNumber: "", dueDate: "", folderName: "" };
const MISSING_FIELD_MESSAGE = "Not found in the pasted email — enter manually.";

// Exact date equality against the real appointment, with the provider's
// first meaningful word as a tiebreaker when more than one encounter falls
// on the same date. Cortney can always override via the dropdown.
function bestEncounterMatch(extractedAppointment, encounters, claimedPatientIds) {
  if (!extractedAppointment.date) return null;
  const sameDate = encounters.filter(
    (encounter) => encounter.date === extractedAppointment.date && !claimedPatientIds.has(encounter.patientId)
  );
  if (sameDate.length <= 1) return sameDate[0] || null;

  const providerWord = String(extractedAppointment.provider || "")
    .split(/[\s,]+/)
    .find((word) => word.length > 2);
  if (!providerWord) return sameDate[0];

  const byProvider = sameDate.find((encounter) =>
    String(encounter.provider || "").toLowerCase().includes(providerWord.toLowerCase())
  );
  return byProvider || sameDate[0];
}

export default function FoiaNewRequest({ setAuth, setUserOrg }) {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [pastedText, setPastedText] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [extractedAppointments, setExtractedAppointments] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [matches, setMatches] = useState({});
  const [confirming, setConfirming] = useState(false);
  const selectedMrnRef = useRef(null);

  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      setSearchCompleted(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    setSearchCompleted(false);
    const timer = setTimeout(() => {
      searchFoiaPatients({
        name: q,
        appointments: extractedAppointments.map(({ date, provider }) => ({ date, provider })),
        stationId: form.stationNumber.trim(),
      })
        .then((data) => {
          if (cancelled) return;
          setSearchResults(data?.patients || []);
          setSearchCompleted(true);
        })
        .catch((err) => {
          if (!cancelled) {
            toast({ title: "Couldn't search patients", description: err.message, variant: "destructive" });
          }
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, extractedAppointments, form.stationNumber]);

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const result = await parseFoiaEmail(pastedText);
      setForm({
        trackingNumber: result.trackingNumber || "",
        patientName: result.patientName || "",
        vamc: result.vamc || "",
        stationNumber: result.stationNumber || "",
        dueDate: result.dueDate || "",
        folderName: result.folderName || result.trackingNumber || "",
      });
      const appointments = result.appointments || [];
      setExtractedAppointments(appointments);
      setMissingFields(result.missingFields || []);
      setSearchQuery(result.patientName || "");
      setExtracted(true);
      selectedMrnRef.current = null;
      setSelectedPatient(null);
      setPatientAppointments([]);
      setMatches({});
    } catch (err) {
      toast({ title: "Couldn't parse that email", description: err.message, variant: "destructive" });
    } finally {
      setExtracting(false);
    }
  };

  const updateField = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const fieldError = (key) =>
    missingFields.includes(key) && !String(form[key] ?? "").trim() ? MISSING_FIELD_MESSAGE : undefined;

  const selectPatient = async (patient) => {
    selectedMrnRef.current = patient.mrn;
    setSelectedPatient(patient);
    setMatches({});
    setPatientAppointments([]);
    setLoadingAppointments(true);
    try {
      const data = await fetchFoiaPatientAppointments(patient.mrn);
      if (selectedMrnRef.current !== patient.mrn) return;
      const encounters = data?.appointments || [];
      setPatientAppointments(encounters);
      const preSelected = {};
      const claimedPatientIds = new Set();
      extractedAppointments.forEach((appt, index) => {
        const match = bestEncounterMatch(appt, encounters, claimedPatientIds);
        if (!match) return;
        preSelected[index] = match.patientId;
        claimedPatientIds.add(match.patientId);
      });
      setMatches(preSelected);
    } catch (err) {
      if (selectedMrnRef.current !== patient.mrn) return;
      toast({ title: "Couldn't load that patient's appointments", description: err.message, variant: "destructive" });
    } finally {
      if (selectedMrnRef.current === patient.mrn) setLoadingAppointments(false);
    }
  };

  const setMatchFor = (index, patientId) => {
    setMatches((prev) => ({ ...prev, [index]: patientId }));
  };

  const appointmentOptions = useMemo(() => {
    const baseLabel = (encounter) => `${formatDate(encounter.date)} — ${encounter.provider}`;
    const counts = patientAppointments.reduce((acc, encounter) => {
      acc[baseLabel(encounter)] = (acc[baseLabel(encounter)] || 0) + 1;
      return acc;
    }, {});
    return patientAppointments.map((encounter) => {
      const label = baseLabel(encounter);
      const extras =
        counts[label] > 1 ? [encounter.time, encounter.stationNumber && `Station ${encounter.stationNumber}`] : [];
      return {
        value: encounter.patientId,
        label: [label, ...extras.filter(Boolean)].join(" · "),
      };
    });
  }, [patientAppointments]);

  const matchedEncounters = useMemo(
    () =>
      extractedAppointments.map((_, index) =>
        patientAppointments.find((encounter) => encounter.patientId === matches[index])
      ),
    [extractedAppointments, patientAppointments, matches]
  );
  const allMatched = matchedEncounters.length > 0 && matchedEncounters.every(Boolean);
  const allDistinct = new Set(matchedEncounters.map((encounter) => encounter?.patientId)).size === matchedEncounters.length;
  const canConfirm =
    extracted &&
    selectedPatient &&
    allMatched &&
    allDistinct &&
    form.trackingNumber.trim() &&
    form.patientName.trim() &&
    form.dueDate.trim();

  const handleConfirm = async () => {
    if (!allMatched) {
      toast({
        title: "Selected appointment is no longer available",
        description: "Pick the matching appointment for each row again.",
        variant: "destructive",
      });
      return;
    }
    setConfirming(true);
    try {
      const appointments = extractedAppointments.map((apptLine, index) => {
        const encounter = matchedEncounters[index];
        return {
          appointmentDate: encounter.date,
          appointmentTime: encounter.time || apptLine.time || null,
          providerName: encounter.provider || apptLine.provider || null,
          matchedPatientId: encounter.patientId,
          matchedAppointmentId: encounter.appointmentId || null,
        };
      });

      const { request } = await createFoiaRequest({
        externalTrackingNumber: form.trackingNumber.trim(),
        mrn: selectedPatient.mrn,
        dueDate: form.dueDate,
        stationId: form.stationNumber.trim() || null,
        vamc: form.vamc.trim() || null,
        sharepointFolderName: form.folderName.trim() || form.trackingNumber.trim(),
        appointments,
      });
      navigate(`/foia/requests/${request.id}`);
    } catch (err) {
      if (err.status === 409) {
        toast({
          title: "That request already exists",
          description: "A FOIA request with this tracking number was already logged.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Couldn't confirm the match", description: err.message, variant: "destructive" });
      }
    } finally {
      setConfirming(false);
    }
  };

  return (
    <FoiaPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
      <button
        type="button"
        onClick={() => navigate("/foia")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 mb-4"
      >
        <ArrowLeft width={15} height={15} />
        All FOIA requests
      </button>

      <PageHeader title="New FOIA Request" subtitle="Paste the COR's email, confirm the match, and log the request." />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="foia-paste-box">
          <Heading as="h2" size="x-small" className="mb-2">
            1. Paste the COR's email
          </Heading>
          <FieldTextarea
            label="Email contents"
            placeholder="Paste the full text of the COR's FOIA request email here…"
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            rows={10}
          />
          <div className="mt-3">
            <Button icon={Mail} loading={extracting} onClick={handleExtract} disabled={!pastedText.trim()}>
              Extract
            </Button>
          </div>
        </section>

        <section>
          <Heading as="h2" size="x-small" className="mb-2">
            2. Review the extracted details
          </Heading>
          {!extracted ? (
            <Banner variant="info">Click Extract to pre-fill this form.</Banner>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FieldText
                label="Tracking number"
                value={form.trackingNumber}
                onValueChange={updateField("trackingNumber")}
                error={fieldError("trackingNumber")}
              />
              <FieldText
                label="Due date"
                value={form.dueDate}
                onValueChange={updateField("dueDate")}
                placeholder="YYYY-MM-DD"
                error={fieldError("dueDate")}
              />
              <FieldText
                label="Patient name"
                value={form.patientName}
                onValueChange={updateField("patientName")}
                error={fieldError("patientName")}
              />
              <FieldText
                label="Station number"
                value={form.stationNumber}
                onValueChange={updateField("stationNumber")}
                error={fieldError("stationNumber")}
              />
              <FieldText
                label="VAMC"
                value={form.vamc}
                onValueChange={updateField("vamc")}
                error={fieldError("vamc")}
                className="sm:col-span-2"
              />
              <FieldText
                label="Folder name"
                value={form.folderName}
                onValueChange={updateField("folderName")}
                error={fieldError("folderName")}
                description="Defaults to the tracking number if the email didn't name one."
                className="sm:col-span-2"
              />
            </div>
          )}
        </section>
      </div>

      {extracted && (
        <section className="mt-8">
          <Heading as="h2" size="x-small" className="mb-2">
            3. Find the patient
          </Heading>
          <p className="text-xs text-gray-500 mb-2">
            Name alone doesn't uniquely identify a patient — double-check the MRN before confirming.
          </p>
          <div className="max-w-sm">
            <FieldText
              icon={Search}
              label="Patient name"
              placeholder="Search by name"
              value={searchQuery}
              onValueChange={setSearchQuery}
              aria-label="Search for a patient"
            />
          </div>

          {searching && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-400">
              <LoadingAnimation size={16} />
              Searching…
            </div>
          )}

          {!searching && searchResults.length > 0 && (
            <div className="mt-3 flex flex-col gap-2">
              {searchResults.map((patient) => (
                <FoiaPatientCandidateCard
                  key={patient.mrn}
                  patient={patient}
                  emailAppointments={extractedAppointments}
                  stationNumber={form.stationNumber.trim()}
                  selected={selectedPatient?.mrn === patient.mrn}
                  onSelect={selectPatient}
                />
              ))}
            </div>
          )}

          {!searching && searchCompleted && searchResults.length === 0 && (
            <p className="mt-3 text-sm text-gray-500">No patient match. Search manually.</p>
          )}
        </section>
      )}

      {selectedPatient && (
        <section className="mt-8">
          <Heading as="h2" size="x-small" className="mb-3">
            4. Match each appointment
          </Heading>
          {loadingAppointments ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <LoadingAnimation size={16} />
              Loading this patient's appointments…
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
              <table className="foia-match-table foia-match-table--edge">
                <thead>
                  <tr>
                    <th>Date (from email)</th>
                    <th>Provider (from email)</th>
                    <th>Matched appointment</th>
                  </tr>
                </thead>
                <tbody>
                  {extractedAppointments.map((line, index) => (
                    <tr key={index}>
                      <td>{line.date ? formatDate(line.date) : <Badge variant="warning" size="small">Not found</Badge>}</td>
                      <td>{line.provider || <Badge variant="warning" size="small">Not found</Badge>}</td>
                      <td style={{ minWidth: 240 }}>
                        <FieldSelect
                          items={appointmentOptions}
                          value={matches[index] || null}
                          onChange={(value) => setMatchFor(index, value)}
                          placeholder="Select the matching appointment"
                          size="small"
                          triggerMinWidth="100%"
                          aria-label={`Matched appointment for row ${index + 1}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <div className="mt-8 flex items-center gap-3">
        <Button icon={Check} disabled={!canConfirm} loading={confirming} onClick={handleConfirm}>
          Confirm match
        </Button>
        {!canConfirm && extracted && (
          <span className="text-xs text-gray-400">Select a patient and match every appointment to a different visit before confirming.</span>
        )}
      </div>
    </FoiaPageShell>
  );
}
